import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import StepperGuest from 'components/stepperGuest/index';
import ReserveCardGuest from 'components/reserveCardGuest/index';
import Linked from 'components/linked/index';
import Path, { ERROR_MESSAGE_TRANSITION } from 'constants/routePath';
import { GUEST_RESERVE_SERVICE_STEP } from 'constants/reservation';
import { useDispatch } from 'react-redux';
import { messageModalActions } from 'slices/messageModalSlice';
import Title from '../../constants/title';
import { Helmet } from 'react-helmet';

const GuestComplete = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { shopAndDateInfo, carInfo, userInfo, reservationResponse } = location.state || {};
  const reservationNumber = reservationResponse?.body?.yykNo;
  const errorMsg = reservationResponse?.displayMessage;

  useEffect(() => {
    if (!location.state?.reservationResponse) {
      const message = ERROR_MESSAGE_TRANSITION;
      dispatch(messageModalActions.showMessageModal({ message }));
      navigate(Path.GUEST_RESERVATION);
      return;
    }
  }, [location.state, navigate]);

  return (
    <div className="mt-4 lg:px-20">
      <Helmet>
        <title>{Title.GUEST_RESERVATION}</title>
      </Helmet>
      <StepperGuest
        labels={GUEST_RESERVE_SERVICE_STEP}
        previousStep={location.state?.previousStep}
        activeStep={4}
        fullWidth={true}
        className={{
          stepperClass: 'max-sm:mx-8 sm:mx-36',
          bottomClass: 'mx-0 h-full w-full text-left',
        }}
        enableLabelUnderline={false}
      />
      <div className="mt-12 mb-4 justify-center text-center">
        <h2 className="text-lg mb-6 font-bold">予約が完了しました</h2>
        <p className="text-sm mt-2">
          下記の内容で予約を承りました。
          <br />
          表示されている予約番号をお控えください。
        </p>
      </div>
      <div className="mt-8 flex justify-center">
        <div className="w-full max-w-[960px]">
        <ReserveCardGuest
          reserveData={{
            reservationNumber: reservationNumber,
            date: shopAndDateInfo?.date,
            time: shopAndDateInfo?.time,
            shopName: shopAndDateInfo?.shop?.shopName,
            carInfo: {
              manufacturer: carInfo?.manufacturer,
              carModel: carInfo?.carModel,
              ...(carInfo?.year && { year: carInfo.year }),
              ...(carInfo?.mileage && {
                mileage: typeof carInfo.mileage === 'string'
                  ? carInfo.mileage.replace(/(\d)(?=(\d{3})+$)/g, '$1,')
                  : carInfo.mileage,
              }),
            },
            userInfo: {
              email: userInfo?.email,
              name: `${userInfo?.lastName} ${
                userInfo?.firstName
              }`.trim(),
              phoneNumber: userInfo?.phoneNumber,
            },
          }}
        />
        </div>
      </div>
      {errorMsg && (
        <div className="text-red-500 mt-4 text-center">{errorMsg}</div>
      )}
      <div className="mt-4 mb-12 flex justify-center sm:mt-6">
        <div className="w-full max-w-[960px]">
          <div className="flex items-center border border-line bg-background-sub py-3">
            <div className="flex flex-col ml-2">
              <span className="text-sm mb-2 font-bold">
                予約の確認・キャンセルについて
              </span>
              <span className="text-sm">
                ご入力いただいたメールアドレスに予約完了メールをお送りいたしました。予約の確認・キャンセルはメール内のURLから行えます。
              </span>
            </div>
          </div>
          <div className="text-[14px] mt-4 whitespace-pre-wrap font-medium sm:mt-6">
            <label>
              【注意事項】
            </label>
            <ul className="list-none pl-0 mt-2">
              <li className="flex items-start mb-2">
                <span className="min-w-[1em]">1.</span>
                <span className="break-words">
                  時間には余裕をもってご来店いただき、受付をお願い致します。<br />
                  (開店時刻と同時刻にご予約いただいた場合は、開店時刻にて受付をお願いします。)
                </span>
              </li>
              <li className="flex items-start mb-2">
                <span className="min-w-[1em]">2.</span>
                <span className="break-words">
                  ご予約のお時間に、連絡なくご来店いただけない場合はキャンセル扱いとさせていただきます。
                </span>
              </li>
              <li className="flex items-start mb-2">
                <span className="min-w-[1em]">3.</span>
                <span className="break-words">
                  WEBからのキャンセルの受付は、作業予約日の前日１２時までとさせていただきます。以降は店舗に直接電話連絡をお願いいたします。
                </span>
              </li>
              <li className="flex items-start mb-2">
                <span className="min-w-[1em]">4.</span>
                <span className="break-words">
                  ご予約の内容について、店舗より電話にて確認をさせていただく場合がございます。
                </span>
              </li>
              <li className="flex items-start mb-2">
                <span className="min-w-[1em]">5.</span>
                <span className="break-words">
                  ご指定のメールアドレスに確認メールを送信いたしますが、念のため予約番号をお控えください。
                </span>
              </li>
              <li className="flex items-start mb-2">
                <span className="min-w-[1em]">6.</span>
                <span className="break-words">
                  携帯メールのドメイン指定受信等（迷惑メール設定）をされている方は、以下のメールアドレスからの受信ができる様に、設定をお願いいたします。<br />
                  送信元アドレス：rsv-info@autobacs.com
                  ※設定を行わない場合、予約受付通知・前日のご案内などのメールが届かない場合がございます。
                </span>
              </li>
              <li className="flex items-start">
                <span className="min-w-[1em]">7.</span>
                <span className="break-words">
                  不正改造車に対する作業はお断りする場合がございます。あらかじめご了承ください。
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="mt-8 mb-12 flex justify-center sm:mb-24">
        <Linked
          text="オートバックスカーズの車買取について"
          onClick={() =>
            window.open(
              'https://www.autobacs.com/static_html/cars/kaitori/top.html'
            )
          }
          isLine
          linkIcon="R"
          className="font-bold"
        />
      </div>
    </div>
  );
};

export default GuestComplete;
